import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button, Badge, LoadingSpinner, Alert } from '../components/ui';
import { 
  FiBarChart, 
  FiDatabase, 
  FiUsers, 
  FiCalendar, 
  FiFolderPlus, 
  FiTrendingUp,
  FiActivity,
  FiSettings
} from 'react-icons/fi';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 150,
    totalBookings: 89,
    totalProjects: 23,
    totalSIGs: 8,
    emailsSent: 234
  });

  // Check if user is admin
  useEffect(() => {
    if (currentUser?.User_Role !== 'ATL_ADMIN') {
      window.location.href = '/';
    }
  }, [currentUser]);

  // Fetch dashboard data
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchDashboardStats();
      fetchRecentActivity();
    }
  }, [activeTab]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/dashboard-stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/recent-activity');
      const data = await response.json();
      if (data.success) {
        setRecentActivity(data.activities);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      // Fallback to mock data if API fails
      setRecentActivity([
        { action: 'New user registration', user: 'John Doe', time: '2 minutes ago', type: 'user' },
        { action: 'Equipment booking approved', user: 'Jane Smith', time: '15 minutes ago', type: 'booking' },
        { action: 'Project created', user: 'Alex Johnson', time: '1 hour ago', type: 'project' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color = 'primary' }) => (
    <Card hover={false} className="group">
      <Card.Content className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-serif text-primary-500 dark:text-gray-400 mb-1">
              {title}
            </p>
            <p className="text-3xl font-elegant text-primary-900 dark:text-white">
              {value}
            </p>
            {trend && (
              <div className="flex items-center mt-2">
                <FiTrendingUp className="w-4 h-4 text-success-600 mr-1" />
                <span className="text-sm font-sans text-success-600">+{trend}%</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-${color}-100 dark:bg-${color}-900/20 group-hover:scale-110 transition-transform`}>
            <Icon className={`w-6 h-6 text-${color}-600 dark:text-${color}-400`} />
          </div>
        </div>
      </Card.Content>
    </Card>
  );

  const TabButton = ({ tab, isActive, onClick }) => (
    <Button
      onClick={onClick}
      variant={isActive ? 'primary' : 'ghost'}
      className="w-full justify-center"
      icon={tab.icon}
    >
      {tab.name}
    </Button>
  );

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FiBarChart },
    { id: 'database', name: 'Database Management', icon: FiDatabase }
  ];

  const renderTabContent = () => {
    if (loading) {
      return (
        <Card className="min-h-96">
          <Card.Content className="flex items-center justify-center h-96">
            <LoadingSpinner size="lg" text="Loading dashboard data..." />
          </Card.Content>
        </Card>
      );
    }

    switch (activeTab) {
      case 'overview':
  return (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={FiUsers}
                trend={12}
                color="primary"
              />
              <StatCard
                title="Active Bookings"
                value={stats.totalBookings}
                icon={FiCalendar}
                trend={8}
                color="success"
              />
              <StatCard
                title="Projects"
                value={stats.totalProjects}
                icon={FiFolderPlus}
                trend={15}
                color="warning"
              />
              <StatCard
                title="Student Groups"
                value={stats.totalSIGs}
                icon={FiUsers}
                trend={5}
                color="primary"
              />
            </div>

            {/* Recent Activity */}
            <Card>
              <Card.Header>
                <Card.Title>Recent Activity</Card.Title>
                <Card.Description>Latest actions in the system</Card.Description>
              </Card.Header>
              <Card.Content>
                <div className="space-y-4">
                  {recentActivity.length === 0 ? (
                    <div className="text-center py-8">
                      <FiActivity className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
                      <p className="font-literary text-primary-500 dark:text-gray-400">No recent activity found</p>
                    </div>
                  ) : (
                    recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-primary-100 dark:border-gray-700 last:border-b-0">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/20">
                          {activity.type === 'user' && <FiUsers className="w-4 h-4 text-primary-600 dark:text-primary-400" />}
                          {activity.type === 'booking' && <FiCalendar className="w-4 h-4 text-success-600 dark:text-success-400" />}
                          {activity.type === 'project' && <FiFolderPlus className="w-4 h-4 text-warning-600 dark:text-warning-400" />}
                          {activity.type === 'group' && <FiUsers className="w-4 h-4 text-info-600 dark:text-info-400" />}
                        </div>
                        <div>
                          <p className="font-serif text-primary-900 dark:text-white">{activity.action}</p>
                          <p className="text-sm font-sans text-primary-500 dark:text-gray-400">by {activity.user}</p>
          </div>
        </div>
                      <Badge variant="neutral" size="sm">{activity.time}</Badge>
                    </div>
                    ))
                  )}
                </div>
              </Card.Content>
            </Card>
      </div>
        );

      case 'database':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-elegant text-primary-900 dark:text-white mb-2">Database Management</h2>
              <p className="font-literary text-primary-600 dark:text-gray-300">System administration tools</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'User Management', icon: FiUsers, description: 'Manage user accounts and permissions', link: '/database/user' },
                { name: 'Account Approvals', icon: FiUsers, description: 'Review and approve pending user registrations', link: '/admin/account-approvals' },
                { name: 'Booking Management', icon: FiCalendar, description: 'Oversee equipment and venue bookings', link: '/database/booking' },
                { name: 'Project Management', icon: FiFolderPlus, description: 'Manage collaborative projects', link: '/database/projects' },
                { name: 'Activity Management', icon: FiActivity, description: 'Control live activities and events', link: '/database/activity' },
                { name: 'Database Management', icon: FiDatabase, description: 'System administration tools', link: '/admin-dashboard' },
                { name: 'Token Management', icon: FiSettings, description: 'Manage API tokens and access keys', link: '/database/tokens' }
              ].map((tool, index) => (
                <Card 
                  key={index} 
                  hover 
                  className="cursor-pointer transition-all duration-300 hover:shadow-soft-lg hover:-translate-y-1 border-2 border-transparent hover:border-primary-200 dark:hover:border-primary-700"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Navigating to:', tool.link);
                    try {
                      if (tool.link && tool.link !== '#') {
                        navigate(tool.link);
                      }
                    } catch (error) {
                      console.error('Navigation error:', error);
                      // Fallback to window location if navigate fails
                      window.location.href = tool.link;
                    }
                  }}
                >
                  <Card.Content className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/20 mr-4 transition-all duration-200 group-hover:scale-110">
                        <tool.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      </div>
                      <h3 className="font-serif text-primary-900 dark:text-white">{tool.name}</h3>
                    </div>
                    <p className="font-literary text-primary-600 dark:text-gray-300 text-sm">
                      {tool.description}
                    </p>
                    <div className="mt-4 flex items-center text-primary-500 dark:text-primary-400 text-sm">
                      <span>Click to manage →</span>
                    </div>
                  </Card.Content>
                </Card>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (currentUser?.User_Role !== 'ATL_ADMIN') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert type="error" title="Access Denied">
          You don't have permission to access the admin dashboard.
        </Alert>
                </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 shadow-soft border-b border-primary-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-elegant text-primary-900 dark:text-white mb-2">
                  Admin Dashboard
                </h1>
                <p className="font-literary text-primary-600 dark:text-gray-300 text-lg">
                  Welcome back, {currentUser?.First_Name || 'Admin'}
                </p>
                </div>

            </div>
                </div>
              </div>
            </div>

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card padding="lg">
              <div className="space-y-2">
                {tabs.map((tab) => (
                  <TabButton
                    key={tab.id}
                    tab={tab}
                    isActive={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  />
                ))}
                </div>
            </Card>
            </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {renderTabContent()}
            </div>
          </div>
      </div>
    </div>
  );
};

export default AdminDashboard;